/**
 * VerticalSliceScene - parallel gameplay rewrite path.
 */

import Phaser from 'phaser';
import { Button } from '../ui/Button';
import {
  COLORS,
  DURATIONS,
  GAME_HEIGHT,
  GAME_WIDTH,
  SAFE_AREA,
} from '../config';
import { scoreSystem } from '../systems/ScoreSystem';
import { audioSystem } from '../systems/AudioSystem';
import { Player } from '../entities/Player';
import { WeaponSystem } from '../systems/WeaponSystem';
import { SliceInputSystem } from '../systems/SliceInputSystem';
import { SliceSpawnSystem } from '../systems/SliceSpawnSystem';
import { getVerticalSliceConfig } from '../levels/VerticalSliceData';
import { HUDV2 } from '../ui/HUDV2';
import { ResultsSceneData } from './ResultsScene';
import { Gate } from '../entities/Gate';
import { WeaponType } from '../utils/constants';
import { logSceneEvent } from '../utils/automation';
import { trackGateChoice, trackLevelComplete, trackLevelFail, trackLevelStart } from '../utils/analytics';
import { destroyParticlePool, getParticlePool, initParticlePool } from '../systems/ParticlePool';
import { parseSliceRouteOptions, SliceRunMode } from '../utils/sliceMode';
import { computeHitStopTimeScale } from './verticalSliceMath';
import {
  clampToTrackBounds,
  createGameplayBackground,
  createGameplayTrack,
  createParallaxLines,
  createPauseIconButton,
  setPauseButtonIcon,
  updateParallaxLines,
} from './shared/gameplayScaffold';
import {
  DeterministicHookBindings,
  installDeterministicHooks,
  uninstallDeterministicHooks,
} from './shared/deterministicHooks';
import { destroyIfPresent } from './shared/shutdownHelpers';

declare global {
  interface Window {
    render_game_to_text?: () => string;
    advanceTime?: (ms: number) => void;
  }
}

interface VerticalSliceTelemetry {
  timeToFirstShotMs: number | null;
  timeToFirstEnemyKillMs: number | null;
  timeToFirstBarrierClearMs: number | null;
  timeToFirstDecisionMs: number | null;
  shotsFired: number;
  enemyKills: number;
  barriersCleared: number;
  decisionCount: number;
  avgDecisionSpacingMs: number;
  runDurationMs: number;
  deathsByBarrier: number;
}

export class VerticalSliceScene extends Phaser.Scene {
  private static readonly DETERMINISTIC_STEP_MS = 1000 / 60;
  private static readonly HIT_STOP_COOLDOWN_MS = 400;
  /** Finale visual style lock for current slice sign-off. */
  private static readonly FINALE_VISUAL_STYLE_LOCK = 'slice-v1-final';

  private readonly levelConfig = getVerticalSliceConfig();

  private isPaused: boolean = false;
  private isResolved: boolean = false;

  private currentLevel: number = 1;
  private currentTier: number = 1;
  private scrollSpeed: number = 95;
  private runMode: SliceRunMode = 'manual';

  private trackPadding: number = 20;
  private trackLeftBound: number = 0;
  private trackRightBound: number = GAME_WIDTH;

  private player!: Player;
  private inputSystem!: SliceInputSystem;
  private weaponSystem!: WeaponSystem;
  private spawnSystem!: SliceSpawnSystem;
  private hud!: HUDV2;
  private pauseButton: Button | null = null;

  private parallaxLines: Phaser.GameObjects.Line[] = [];

  private finaleBase: Phaser.GameObjects.Container | null = null;
  private finaleStarted: boolean = false;

  private score: number = 0;

  private runStartMs: number = 0;
  private telemetry: VerticalSliceTelemetry = {
    timeToFirstShotMs: null,
    timeToFirstEnemyKillMs: null,
    timeToFirstBarrierClearMs: null,
    timeToFirstDecisionMs: null,
    shotsFired: 0,
    enemyKills: 0,
    barriersCleared: 0,
    decisionCount: 0,
    avgDecisionSpacingMs: 0,
    runDurationMs: 0,
    deathsByBarrier: 0,
  };
  private decisionMarkersMs: number[] = [];

  private hitStopUntilMs: number = 0;
  private lastHitStopAtMs: number = -Infinity;
  private timeScale: number = 1;

  private deterministicMode: boolean = false;
  private deterministicTimeMs: number = 0;
  private manualStepInProgress: boolean = false;
  private deterministicHooks: DeterministicHookBindings | null = null;
  private autoplayDriftDirection: -1 | 1 = -1;
  private autoplayLastFlipProgress: number = 0;

  constructor() {
    super({ key: 'VerticalSliceScene' });
  }

  init(data: { level?: number; tier?: number; runMode?: SliceRunMode } = {}): void {
    const routeOptions = typeof window !== 'undefined'
      ? parseSliceRouteOptions(window.location.search)
      : { useSliceMode: false, autostart: false, runMode: 'manual' as SliceRunMode };

    this.currentLevel = data.level ?? 1;
    this.currentTier = data.tier ?? 1;
    this.runMode = data.runMode ?? routeOptions.runMode;
    this.isPaused = false;
    this.isResolved = false;
    this.score = 0;
    this.scrollSpeed = 95;
    this.finaleBase = null;
    this.finaleStarted = false;
    this.hitStopUntilMs = 0;
    this.lastHitStopAtMs = -Infinity;
    this.timeScale = 1;
    this.autoplayDriftDirection = -1;
    this.autoplayLastFlipProgress = 0;

    this.telemetry = {
      timeToFirstShotMs: null,
      timeToFirstEnemyKillMs: null,
      timeToFirstBarrierClearMs: null,
      timeToFirstDecisionMs: null,
      shotsFired: 0,
      enemyKills: 0,
      barriersCleared: 0,
      decisionCount: 0,
      avgDecisionSpacingMs: 0,
      runDurationMs: 0,
      deathsByBarrier: 0,
    };
    this.decisionMarkersMs = [];

    scoreSystem.setLevel(this.currentLevel, this.currentTier);
  }

  create(): void {
    logSceneEvent('VerticalSliceScene');
    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);
    trackLevelStart(this.currentLevel, this.currentTier);

    audioSystem.playMusic('gameplay');
    initParticlePool(this);

    this.runStartMs = this.time.now;

    this.createBackground();
    this.createTrack();
    this.createParallaxLines();
    this.createPauseButton();

    this.trackLeftBound = this.trackPadding;
    this.trackRightBound = GAME_WIDTH - this.trackPadding;

    this.player = new Player(this, {
      startingSoldiers: 1,
      trackWidth: this.trackRightBound - this.trackLeftBound,
      trackPadding: this.trackPadding,
    });

    this.hud = new HUDV2(this, {
      soldierCount: this.player.getSoldierCount(),
      score: 0,
      progress: 0,
    });

    this.inputSystem = new SliceInputSystem(this, {
      getTrackBounds: () => ({ left: this.trackLeftBound, right: this.trackRightBound }),
      getIsPaused: this.getIsPaused.bind(this),
      keyboardSensitivity: 1.55,
    });

    this.weaponSystem = new WeaponSystem(this, {
      player: this.player,
      getEnemies: () => this.spawnSystem?.getActiveEnemies() ?? [],
      getWeaponBoxes: () => [],
      getIsPaused: this.getIsPaused.bind(this),
      rangeOverride: 545,
      fireRateMultiplier: this.runMode === 'autoplay' ? 1.2 : 1.1,
      primeFirstShot: true,
      onShotFired: ({ timestamp }) => {
        this.telemetry.shotsFired += 1;
        if (this.telemetry.timeToFirstShotMs === null) {
          this.telemetry.timeToFirstShotMs = Math.round(timestamp - this.runStartMs);
        }
      },
    });

    this.spawnSystem = new SliceSpawnSystem({
      scene: this,
      levelConfig: this.levelConfig,
      getScrollSpeed: () => this.scrollSpeed,
      getIsPaused: this.getIsPaused.bind(this),
      getCurrentArmySize: () => this.player.getSoldierCount(),
      trackWidth: this.trackRightBound - this.trackLeftBound,
      trackPadding: this.trackPadding,
    });

    this.setupSpawnEvents();
    this.installAutomationHooks();

    this.events.on('update', this.gameLoop, this);
  }

  setHitStop(durationMs: number, force: boolean = false): void {
    const now = this.time.now;
    if (!force && now - this.lastHitStopAtMs < VerticalSliceScene.HIT_STOP_COOLDOWN_MS) {
      return;
    }

    this.lastHitStopAtMs = now;
    this.hitStopUntilMs = Math.max(this.hitStopUntilMs, now + Math.max(1, durationMs));
  }

  getEffectiveDelta(deltaMs: number): number {
    return deltaMs * this.timeScale;
  }

  private setupSpawnEvents(): void {
    const events = this.spawnSystem.getEvents();

    events.on('enemyKilled', (_enemy: Phaser.GameObjects.GameObject, points: number) => {
      this.telemetry.enemyKills += 1;
      if (this.telemetry.timeToFirstEnemyKillMs === null) {
        this.telemetry.timeToFirstEnemyKillMs = Math.round(this.time.now - this.runStartMs);
      }

      scoreSystem.addKillPoints(points);
      this.score = scoreSystem.getCurrentScore();
      this.hud.updateScore(this.score);
    });

    events.on('gateActivated', (gate: Gate, multiplier: number, soldiersAdded: number) => {
      trackGateChoice(this.currentLevel, Math.round(this.spawnSystem.getProgressPercent() * 100), multiplier);

      if (soldiersAdded > 0) {
        this.player.addSoldiers(soldiersAdded, true);
      }

      scoreSystem.addGateBonus(multiplier, soldiersAdded);
      this.score = scoreSystem.getCurrentScore();

      this.hud.updateSoldierCount(this.player.getSoldierCount());
      this.hud.updateScore(this.score);

      this.showMultiplierPopup(gate.x, gate.y, multiplier);
      this.recordDecisionMarker(this.time.now);
      this.setHitStop(50);
    });

    events.on('barrierDestroyed', (payload: unknown) => {
      const data = payload as {
        rewarded: boolean;
        rewardSoldiers: number;
        rewardScore: number;
      };

      if (data.rewarded) {
        this.telemetry.barriersCleared += 1;
        if (this.telemetry.timeToFirstBarrierClearMs === null) {
          this.telemetry.timeToFirstBarrierClearMs = Math.round(this.time.now - this.runStartMs);
        }

        if (data.rewardSoldiers > 0) {
          this.player.addSoldiers(data.rewardSoldiers, true);
        }
        if (data.rewardScore > 0) {
          scoreSystem.addKillPoints(data.rewardScore);
        }

        this.hud.updateSoldierCount(this.player.getSoldierCount());
        this.score = scoreSystem.getCurrentScore();
        this.hud.updateScore(this.score);

        this.setHitStop(50);
      }
    });

    events.on('barrierSpawned', () => {
      this.recordDecisionMarker(this.time.now);
    });

    events.on('gateRowSpawned', () => {
      this.recordDecisionMarker(this.time.now);
    });
  }

  private gameLoop(time: number, delta: number): void {
    if (this.isPaused || this.isResolved) return;
    if (this.deterministicMode && !this.manualStepInProgress) return;

    this.updateHitStop(time);
    const effectiveDelta = this.getEffectiveDelta(delta);

    this.inputSystem.update(effectiveDelta);
    this.updateAutoplayTarget();
    this.updateTelemetry(time);
    this.applyAnalogMovement(effectiveDelta);

    if (effectiveDelta > 0) {
      this.updateParallaxLines(effectiveDelta);
      this.player.update(effectiveDelta);
      this.spawnSystem.update(effectiveDelta);
      this.weaponSystem.update(time, effectiveDelta);
      this.checkEnemyArmyCollisions();

      this.checkGateCollisions();
      this.checkBarrierBulletCollisions();
      this.checkBarrierArmyCollisions();

      this.updateFinale(effectiveDelta);
    }

    const currentSoldiers = this.player.getSoldierCount();
    if (currentSoldiers !== this.hud.getSoldierCount()) {
      this.hud.updateSoldierCount(currentSoldiers);
    }

    this.hud.updateWeapon(this.weaponSystem.getCurrentWeapon() ?? WeaponType.Pistol);
    this.hud.updateProgress(this.spawnSystem.getProgressPercent());

    if (!this.finaleStarted && this.spawnSystem.getProgressDistance() >= this.levelConfig.finaleDistance) {
      this.startFinale();
    }

    if (!this.isResolved && this.spawnSystem.getIsLevelComplete() && !this.finaleStarted) {
      this.resolveLevelComplete();
    }
  }

  private updateHitStop(nowMs: number): void {
    this.timeScale = computeHitStopTimeScale(nowMs, this.hitStopUntilMs);
  }

  private applyAnalogMovement(effectiveDelta: number): void {
    if (effectiveDelta <= 0) return;

    const targetX01 = this.inputSystem.getTargetX01();
    const targetX = this.trackLeftBound + targetX01 * (this.trackRightBound - this.trackLeftBound);

    const currentX = this.player.getArmyX();
    const deltaX = Math.abs(targetX - currentX);
    const lerpFactor = Phaser.Math.Clamp(0.24 + deltaX / 520, 0.24, this.runMode === 'autoplay' ? 0.42 : 0.36);
    const smoothedX = Phaser.Math.Linear(currentX, targetX, lerpFactor);
    const clampedX = this.clampToTrack(smoothedX, this.player.getFormationRadius() * 2);

    this.player.setArmyX(clampedX);
  }

  private updateAutoplayTarget(): void {
    if (this.runMode !== 'autoplay') return;

    const activeGates = this.spawnSystem.getActiveGates()
      .filter((gate) => gate.getIsActive() && !gate.getIsActivated());
    if (activeGates.length > 0) {
      const bestGate = activeGates.reduce((best, gate) => {
        if (gate.getMultiplier() !== best.getMultiplier()) {
          return gate.getMultiplier() > best.getMultiplier() ? gate : best;
        }
        return gate.y > best.y ? gate : best;
      }, activeGates[0]);
      this.inputSystem.setTargetX01(this.projectWorldXToX01(bestGate.x));
      return;
    }

    const activeBarriers = this.spawnSystem.getActiveBarriers().filter((barrier) => barrier.getIsActive());
    if (activeBarriers.length > 0) {
      const nearestBarrier = activeBarriers.reduce((best, barrier) => barrier.y > best.y ? barrier : best, activeBarriers[0]);
      this.inputSystem.setTargetX01(this.projectWorldXToX01(nearestBarrier.x));
      return;
    }

    const activeEnemies = this.spawnSystem.getActiveEnemies().filter((enemy) => enemy.getIsAlive());
    if (activeEnemies.length > 0) {
      const nearestEnemy = activeEnemies.reduce((best, enemy) => enemy.y > best.y ? enemy : best, activeEnemies[0]);
      this.inputSystem.setTargetX01(this.projectWorldXToX01(nearestEnemy.x));
      return;
    }

    const progress = this.spawnSystem.getProgressPercent();
    if (progress - this.autoplayLastFlipProgress >= 0.08) {
      this.autoplayDriftDirection = this.autoplayDriftDirection === 1 ? -1 : 1;
      this.autoplayLastFlipProgress = progress;
    }

    this.inputSystem.setTargetX01(this.autoplayDriftDirection > 0 ? 0.82 : 0.18);
  }

  private checkGateCollisions(): void {
    const gates = this.spawnSystem.getActiveGates();
    const playerX = this.player.getArmyX();
    const playerY = this.player.getArmyY();

    for (const gate of gates) {
      if (!gate.getIsActive() || gate.getIsActivated()) continue;

      const bounds = gate.getCollisionBounds();
      const withinX = playerX >= bounds.x && playerX <= bounds.x + bounds.width;
      const withinY = playerY >= bounds.y && playerY <= bounds.y + bounds.height;

      if (withinX && withinY) {
        gate.activate(this.player.getSoldierCount());
      }
    }
  }

  private checkBarrierBulletCollisions(): void {
    const bullets = this.weaponSystem.getBullets();
    const barriers = this.spawnSystem.getActiveBarriers();

    for (const barrier of barriers) {
      if (!barrier.getIsActive()) continue;

      for (const bullet of bullets) {
        if (!bullet.getIsActive()) continue;

        const dx = bullet.x - barrier.x;
        const dy = bullet.y - barrier.y;
        const distanceSq = dx * dx + dy * dy;
        const collisionDistance = bullet.getCollisionRadius() + barrier.getCollisionRadius();

        if (distanceSq < collisionDistance * collisionDistance) {
          barrier.takeDamage(bullet.getDamage());
          bullet.deactivate();
          break;
        }
      }
    }
  }

  private checkBarrierArmyCollisions(): void {
    const barriers = this.spawnSystem.getActiveBarriers();
    const playerX = this.player.getArmyX();
    const playerY = this.player.getArmyY();

    for (const barrier of barriers) {
      if (!barrier.getIsActive()) continue;

      const bounds = barrier.getCollisionBounds();
      const withinX = playerX >= bounds.x && playerX <= bounds.x + bounds.width;
      const withinY = playerY >= bounds.y && playerY <= bounds.y + bounds.height;

      if (!withinX || !withinY) continue;

      const soldiersLost = barrier.resolveArmyCollision(this.player.getSoldierCount());
      if (soldiersLost > 0) {
        this.telemetry.deathsByBarrier += soldiersLost;
        this.player.removeSoldiers(soldiersLost);
        this.hud.updateSoldierCount(this.player.getSoldierCount());
        this.setHitStop(50);
      }

      if (this.player.isDefeated()) {
        this.handleGameOver();
      }
    }
  }

  private checkEnemyArmyCollisions(): void {
    const enemies = this.spawnSystem.getActiveEnemies();
    const playerX = this.player.getArmyX();
    const playerY = this.player.getArmyY();
    const playerRadius = this.player.getFormationRadius();

    for (const enemy of enemies) {
      if (!enemy.getIsAlive()) continue;

      const dx = enemy.x - playerX;
      const dy = enemy.y - playerY;
      const distanceSq = dx * dx + dy * dy;
      const combinedRadius = playerRadius + enemy.getCollisionRadius();

      if (distanceSq >= combinedRadius * combinedRadius) continue;

      const damage = Math.max(1, Math.round(enemy.getHp() * 1.2));
      enemy.destroy();
      this.player.removeSoldiers(damage);
      this.hud.updateSoldierCount(this.player.getSoldierCount());
      this.setHitStop(50);

      if (this.player.isDefeated()) {
        this.handleGameOver();
        return;
      }
    }
  }

  private startFinale(): void {
    if (this.finaleStarted) return;

    this.finaleStarted = true;

    const container = this.add.container(GAME_WIDTH / 2, -150);
    container.setDepth(16);
    container.setData('visualStyleLock', VerticalSliceScene.FINALE_VISUAL_STYLE_LOCK);

    const shadow = this.add.ellipse(0, 62, 190, 28, 0x000000, 0.2);
    const frame = this.add.image(0, 0, 'gate-panel');
    frame.setDisplaySize(224, 138);
    frame.setTint(0x4F3138);
    frame.setAlpha(0.95);

    const warningStripe = this.add.graphics();
    warningStripe.fillStyle(0x2A2D38, 0.9);
    warningStripe.fillRoundedRect(-84, -18, 168, 52, 8);
    warningStripe.lineStyle(3, 0x1A1A1A, 0.9);
    warningStripe.strokeRoundedRect(-84, -18, 168, 52, 8);
    warningStripe.lineStyle(3, 0xC94E2D, 0.5);
    for (let x = -76; x < 76; x += 18) {
      warningStripe.beginPath();
      warningStripe.moveTo(x, -16);
      warningStripe.lineTo(x + 12, 32);
      warningStripe.strokePath();
    }

    const coreGlow = this.add.circle(0, 8, 34, COLORS.enemy, 0.24);
    const core = this.add.sprite(0, 8, 'enemy-elite');
    core.setScale(0.9);
    core.play('lobster_idle');

    const leftTurret = this.add.sprite(-74, 10, 'enemy-basic');
    leftTurret.setScale(0.7);
    leftTurret.play('lobster_walk');
    const rightTurret = this.add.sprite(74, 10, 'enemy-basic');
    rightTurret.setScale(0.7);
    rightTurret.flipX = true;
    rightTurret.play('lobster_walk');

    const labelPlate = this.add.rectangle(0, -46, 124, 30, 0x202534).setStrokeStyle(2, 0x525A73, 0.9);
    const label = this.add.text(0, -46, 'CORE BASE', {
      fontFamily: 'Fredoka, Nunito, system-ui, sans-serif',
      fontSize: '17px',
      color: '#FFFFFF',
      fontStyle: 'bold',
      stroke: '#1A1A1A',
      strokeThickness: 3,
    }).setOrigin(0.5);

    container.add([
      shadow,
      frame,
      warningStripe,
      coreGlow,
      core,
      leftTurret,
      rightTurret,
      labelPlate,
      label,
    ]);

    this.tweens.add({
      targets: coreGlow,
      alpha: 0.46,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.tweens.add({
      targets: container,
      y: '-=6',
      duration: 860,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.finaleBase = container;
  }

  private updateFinale(delta: number): void {
    if (!this.finaleBase || this.isResolved) return;

    this.finaleBase.y += (this.scrollSpeed * delta) / 1000;

    const triggerY = this.player.getArmyY() - 84;
    if (this.finaleBase.y >= triggerY) {
      this.playFinaleBreachFx();
      this.setHitStop(80, true);
      this.resolveLevelComplete();
    }
  }

  private playFinaleBreachFx(): void {
    const pool = getParticlePool();
    if (pool && this.finaleBase) {
      pool.spawnBurst(this.finaleBase.x, this.finaleBase.y, 'square', COLORS.enemy, 18, {
        minSize: 4,
        maxSize: 11,
        minVelocity: 120,
        maxVelocity: 240,
        minDuration: 200,
        maxDuration: 360,
        alpha: 0.85,
      });
    }

    this.cameras.main.shake(120, 0.007);
  }

  private resolveLevelComplete(): void {
    if (this.isResolved) return;
    this.isResolved = true;

    audioSystem.playLevelCompleteFanfare();

    const survivingSoldiers = this.player.getSoldierCount();
    const result = scoreSystem.finalizeLevelScore(survivingSoldiers);

    trackLevelComplete(
      this.currentLevel,
      this.currentTier,
      result.breakdown.total,
      survivingSoldiers
    );

    const resultsData: ResultsSceneData = {
      level: this.currentLevel,
      tier: this.currentTier,
      isVictory: true,
      breakdown: result.breakdown,
    };

    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ResultsScene', resultsData);
    });
  }

  private handleGameOver(): void {
    if (this.isResolved) return;
    this.isResolved = true;

    const failPosition = this.spawnSystem?.getProgressPercent() ?? 0;
    trackLevelFail(this.currentLevel, this.currentTier, this.player.getSoldierCount(), failPosition);

    audioSystem.playGameOverSequence();

    const breakdown = scoreSystem.getBreakdown();
    const resultsData: ResultsSceneData = {
      level: this.currentLevel,
      tier: this.currentTier,
      isVictory: false,
      breakdown: {
        kills: breakdown.kills,
        gateBonus: breakdown.gateBonus,
        armyBonus: 0,
        total: breakdown.total,
      },
    };

    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ResultsScene', resultsData);
    });
  }

  private updateTelemetry(nowMs: number): void {
    this.telemetry.runDurationMs = Math.max(0, nowMs - this.runStartMs);

    if (this.telemetry.timeToFirstDecisionMs === null && this.inputSystem.getHasMovedFromCenter()) {
      this.telemetry.timeToFirstDecisionMs = Math.round(this.telemetry.runDurationMs);
    }
  }

  private recordDecisionMarker(markerTimeMs: number): void {
    this.decisionMarkersMs.push(markerTimeMs);
    this.telemetry.decisionCount = this.decisionMarkersMs.length;
    if (this.decisionMarkersMs.length < 2) {
      this.telemetry.avgDecisionSpacingMs = 0;
      return;
    }

    let totalSpacing = 0;
    for (let i = 1; i < this.decisionMarkersMs.length; i += 1) {
      totalSpacing += this.decisionMarkersMs[i] - this.decisionMarkersMs[i - 1];
    }

    this.telemetry.avgDecisionSpacingMs = Math.round(totalSpacing / (this.decisionMarkersMs.length - 1));
  }

  private getPlayerX01(): number {
    const width = Math.max(1, this.trackRightBound - this.trackLeftBound);
    return Phaser.Math.Clamp((this.player.getArmyX() - this.trackLeftBound) / width, 0, 1);
  }

  private projectWorldXToX01(x: number): number {
    const width = Math.max(1, this.trackRightBound - this.trackLeftBound);
    return Phaser.Math.Clamp((x - this.trackLeftBound) / width, 0, 1);
  }

  private createBackground(): void {
    createGameplayBackground(this);
  }

  private createTrack(): void {
    createGameplayTrack(this, {
      trackPadding: this.trackPadding,
      laneDividerBottomY: GAME_HEIGHT - SAFE_AREA.bottom - 180,
      laneDividerAlpha: 0.8,
      laneDividerWidth: 2,
    });
  }

  private createParallaxLines(): void {
    this.parallaxLines = createParallaxLines(this, {
      trackPadding: this.trackPadding,
      lineSpacing: 86,
      lineWidth: 1,
      lineAlpha: 0.35,
      lineSpeedRatio: 0.35,
    });
  }

  private updateParallaxLines(delta: number): void {
    updateParallaxLines(this.parallaxLines, delta, this.scrollSpeed, {
      lineSpacing: 86,
      lineSpeedRatio: 0.35,
    });
  }

  private createPauseButton(): void {
    this.pauseButton = createPauseIconButton(this, {
      isPaused: () => this.isPaused,
      onToggle: () => {
        this.isPaused = !this.isPaused;
        this.updatePauseButtonIcon();
      },
    });
    this.updatePauseButtonIcon();
  }

  private updatePauseButtonIcon(): void {
    if (!this.pauseButton) return;
    setPauseButtonIcon(this.pauseButton, this.isPaused);
  }

  private clampToTrack(x: number, entityWidth: number = 0): number {
    return clampToTrackBounds(x, this.trackLeftBound, this.trackRightBound, entityWidth);
  }

  private showMultiplierPopup(x: number, y: number, multiplier: number): void {
    const popup = this.add.text(x, y, `x${multiplier}`, {
      fontFamily: 'Fredoka, Nunito, system-ui, sans-serif',
      fontSize: '42px',
      color: '#00FF94',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);

    popup.setDepth(70);

    this.tweens.add({
      targets: popup,
      y: y - 90,
      alpha: 0,
      scale: 1.2,
      duration: 500,
      ease: 'Power2',
      onComplete: () => popup.destroy(),
    });
  }

  private getIsPaused(): boolean {
    return this.isPaused;
  }

  private installAutomationHooks(): void {
    this.deterministicHooks = installDeterministicHooks({
      stepMs: VerticalSliceScene.DETERMINISTIC_STEP_MS,
      renderToText: this.renderGameToText.bind(this),
      onAdvanceStep: (stepMs) => {
        this.deterministicMode = true;
        this.manualStepInProgress = true;
        this.deterministicTimeMs += stepMs;
        this.gameLoop(this.deterministicTimeMs, stepMs);
        this.manualStepInProgress = false;
      },
    });
  }

  private renderGameToText(): string {
    const barriers = this.spawnSystem.getActiveBarriers();
    const enemies = this.spawnSystem.getActiveEnemies();
    const gates = this.spawnSystem.getActiveGates();

    const hitStopRemaining = Math.max(0, this.hitStopUntilMs - this.time.now);

    const payload = {
      scene: 'VerticalSliceScene',
      runMode: this.runMode,
      mode: this.isPaused ? 'paused' : this.isResolved ? 'resolved' : 'playing',
      coords: 'origin top-left, +x right, +y down',
      progress: {
        distance: this.spawnSystem.getProgressDistance(),
        trackLength: this.spawnSystem.getTrackLength(),
        percent: this.spawnSystem.getProgressPercent(),
      },
      player: {
        x: this.player.getArmyX(),
        y: this.player.getArmyY(),
        x01: this.getPlayerX01(),
        soldiers: this.player.getSoldierCount(),
      },
      hitStop: {
        active: hitStopRemaining > 0,
        remainingMs: Math.round(hitStopRemaining),
      },
      telemetry: {
        ...this.telemetry,
        runDurationMs: Math.round(this.telemetry.runDurationMs),
      },
      bullets: {
        active: this.weaponSystem.getBullets().filter((bullet) => bullet.getIsActive()).length,
        shotsFired: this.telemetry.shotsFired,
      },
      dda: {
        lastAppliedAtSpawn: this.spawnSystem.getLastDdaAppliedAtSpawn(),
      },
      barriers: barriers.map((barrier) => ({
        x: barrier.x,
        y: barrier.y,
        hp: barrier.getHp(),
        hpLocked: barrier.statsLocked,
        spawnFinalHp: barrier.spawnFinalHp,
      })),
      enemies: enemies.map((enemy) => ({
        x: enemy.x,
        y: enemy.y,
        hp: enemy.getHp(),
      })),
      gates: gates.map((gate) => ({
        x: gate.x,
        y: gate.y,
        multiplier: gate.getMultiplier(),
        active: gate.getIsActive(),
      })),
      weapon: this.weaponSystem.getCurrentWeapon() ?? WeaponType.Pistol,
    };

    return JSON.stringify(payload);
  }

  shutdown(): void {
    this.events.off('update', this.gameLoop, this);

    destroyParticlePool();

    if (this.inputSystem) {
      this.inputSystem.destroy();
    }

    if (this.weaponSystem) {
      this.weaponSystem.destroy();
    }

    if (this.spawnSystem) {
      this.spawnSystem.destroy();
    }

    destroyIfPresent(this.player);
    destroyIfPresent(this.hud);

    if (this.pauseButton) {
      this.pauseButton.destroy();
      this.pauseButton = null;
    }

    destroyIfPresent(this.finaleBase);
    this.finaleBase = null;

    uninstallDeterministicHooks(this.deterministicHooks);
    this.deterministicHooks = null;
  }
}
